<template>
  <main class="app-get-step" id="mainContent">
    <div
      class="app-main-content"
      v-if="store.state.guestLogin.cancelRegistration && store.state.guestLogin.cancelRegistration.descriptions==''">
      <h1>{{ screenData.title }}</h1>
      <p class="cancel-reg">ALL REGISTRATION CANCELLATIONS ARE FINAL.</p>

      <div class="app-content-panels">
        <form class="app-panel-left">
          <p><strong>To process your cancel registration request, you will need:</strong></p>
          <ul class="renew-ready">
            <li>
              <p>
                - A <b>valid email address</b> where you will receive a
                acknowledgement of your cancellation of registration.
              </p>
            </li>
          </ul>
          <BaseButtonGroup>
            <CustomButton @click="handleRouting(true)" class="resend-code">Previous</CustomButton>
            <CustomButton @click="handleRouting(false)">Next</CustomButton>
            <CustomButton v-if="!disableBtn" @click="handleRouting(true)" class="resend-code">Cancel and go to profile</CustomButton>
            <CustomButton :class="{ 'disabled': !disableBtn }" :aria-disabled="!disableBtn ? null : 'true'" class="resend-code"
              @click="goToTxnCart(true)" v-if="disableBtn"> Cancel and go to cart
            </CustomButton>
          </BaseButtonGroup>
        </form>
      </div>
    </div>
    <div class="app-main-content" v-if="store.state.guestLogin.cancelRegistration && store.state.guestLogin.cancelRegistration.descriptions != ''">
      <div class="app-alert-error">
        <ul class="err-codes">
          <li v-for="(description, i) in store.state.guestLogin.cancelRegistration.descriptions" :key="i">
            <p>{{ description }}</p>
            <p class="sr-only">{{ description }}</p>
          </li>
        </ul>
      </div>
      <BaseButtonGroup>
        <CustomButton @click="handleRouting(true)" class="reg-previous"> Previous </CustomButton>
        <CustomButton :class="{ 'disabled': !disableBtn }" :aria-disabled="!disableBtn ? null : 'true'"
          @click="goToTxnCart(true)" v-if="store.state.guestLogin.cartTransactions && store.state.guestLogin.cartTransactions.cartTransactions.length > 0">Cancel and go to cart
        </CustomButton>
        <CustomButton @click="handleRouting(true)" v-else>
          Cancel and go to profile
        </CustomButton>
      </BaseButtonGroup>
    </div>
  </main>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import appConfig from '@/app.config';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import CustomButton from '../../components/CustomButton.vue';
import * as COMMONS from '../../store/_commons';

// Component  name and page metadata 
defineOptions({
  name: 'GetReady',
  page: {
    title: `Get Ready | ${appConfig.shortName}`,
    meta: [
      {
        name: 'description',
        content: 'First step (Get Ready) for cancelling your registration'
      }
    ]
  }
});

const store = useStore();
const router = useRouter();
const emit = defineEmits(['getStep']);

// Reactive data with ref
const stepNum = ref(1);
const screenData = ref({});

// Computed properties
const isBusinessUser = computed(() => store.state.guestLogin.isBusinessUser);

// Profile route based on user type
const profileRouteName = computed(() => {
  return isBusinessUser.value ? 'BusinessProfile' : 'Profile';
});

const disableBtn = computed(() => {
  let btn = false;
  if (store.state.guestLogin.cartTransactions) {
    btn = store.state.guestLogin.cartTransactions.cartTransactions.length > 0;
  }
  return btn;
});

// Lifecycle hooks
onMounted(() => {
  document.body.setAttribute('tabindex', '1');
  document.body.focus();
  document.body.removeAttribute('tabindex');

  window.scroll(0, 0);
  screenData.value = store.state.STEPS[stepNum.value];
  document.getElementById('mainContent').focus();
});

// Methods 
const getCreateTransactionService = () => {
  const isEligible = store.state.guestLogin.cancelRegistration.isEligible;
  store.dispatch(COMMONS.GUEST_CREATETRANSACTION, isEligible);
  // Reset isEligible after passing it
  store.commit('guestLogin/setState', { 
    property: 'cancelRegistration', 
    value: { ...store.state.guestLogin.cancelRegistration, isEligible: false } 
  });
};

const getAddTransaction = () => {
  const isEligible = store.state.guestLogin.cancelRegistration.isEligible;
  store.dispatch(COMMONS.GUEST_ADDTRANSACTION, isEligible);
  // Reset isEligible after passing it
  store.commit('guestLogin/setState', { 
    property: 'cancelRegistration', 
    value: { ...store.state.guestLogin.cancelRegistration, isEligible: false } 
  });
};

const handleRouting = (isPrev = false) => {
  if (isPrev) {
    router.push({ name: profileRouteName.value });
  } else {
    if (store.state.guestLogin.cartTransactions && store.state.guestLogin.cartTransactions.cartTransactions.length > 0) {
      getAddTransaction();
    } else {
      getCreateTransactionService();
    }
    emit('getStep', 2);
  }
};

const goToTxnCart = () => {
  store.state.guestLogin.step = 4;
  emit('getStep', 4);
};
</script>


<style scoped lang="scss">
  .app-alert-error {
    display: block;
    margin-top: 20px;
    margin-bottom: 2rem;
    font-size: 18px;
    line-height: 28px;
    padding-left: 1rem;
    padding-right: 10px;
    ul {
      padding: 10px;
    }
  }
  .app-content-panels {
    margin-top: 1rem;
    .resend-code{
      background-color: white;
      color: var(--ri-blue);

      &:hover{
        background-color: var(--ri-lighter-blue);
        color: var(--ri-darker-blue);
      }
    }
  }
  .renew-ready {
    margin-bottom: 2rem;
  }
  .app-button-group {
    margin-top: 1rem;
    button {
      margin-bottom: 1.125em;
      &:not(:last-child) {
        margin-right: 1em;
      }
      &.disabled {
        cursor: not-allowed;
        opacity: 0.7;
      }
    }
  }
  .err-codes {
    list-style: initial;
    margin-left: 1rem;
  }

  .cancel-reg{
    color: black;
    font-size: 18px;
    font-weight: 600;
  }
  .reg-previous{
    background-color: white;
    color: var(--ri-blue);

    &:hover{
      background-color: var(--ri-lighter-blue);
      color: var(--ri-darker-blue);
    }
  }
</style>
